import { useLocation, useParams } from "react-router-dom";
import { Breadcrumb } from "../../../../../../components/ProductsLayout/SharedProductsLayout";
import { LinkUnstyled } from "../../../../../../components/LinkUnstyled/LinkUnstyled";
import { PageWrapper } from "../../../../../../layout/portalPageLayout";
import { useRoutePath } from "../../../../../../util/Routing";
import {
  EditButton,
  PrimaryButtonWithPlusIcon,
} from "../../../../../../components/Buttons/Buttons";
import { TemplatesNav, TemplatesNavWithPadding } from "../TemplatesNav";
import {
  TablePlaceholder,
  toTitleCase,
  useStoreState,
} from "../../../../../../util/util";
import { useMdmEntityDisplayNames } from "../../../../../../util/mdmEntityDisplayNames";
import useSWR from "swr";
import type {
  AttributeTemplateSchema,
  SupportedAssetCategoryType,
  TDSGeneratedAssetRequestSchema,
  TdsGeneratedAssetSchema,
} from "../../../../../../types/types.PIM";
import type { AxiosError } from "axios";
import { SlideOut } from "../../../../../../components/SlideOut/SlideOut";
import { ContentWrapper } from "../../../../../../layout/publicPageLayout";
import { Table } from "../../../../../../components/Table/Table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ToggleSwitch } from "../../../../../../components/ToggleSwitch/ToggleSwitch";
import { ConfigureGeneratedAssets } from "./ConfigureGeneratedAssets";
import { endpoints } from "../../../../../../endpoints";
import { DelayedSpinner } from "../../../../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../../../../components/Error";
import axios from "axios";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import { LoadingIcon } from "../../../../../../components/Icons/Icons";
import type { Getter } from "@tanstack/react-table";
import type {
  OptionType,
  TemplateLocationState,
} from "../../../../../../types/types";
import type { AssetCategory } from "../../SellarAdminPIMAssets/util/AssetsUtil";
import { spacingTokens } from "../../../../../../designTokens";

export const GeneratedAssetsSettingsPage = () => {
  const { adminPath } = useRoutePath();
  const { template_id } = useParams<{ template_id: string }>();
  const { tenant_id } = useStoreState();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();
  const { t } = useTranslation();
  const location = useLocation();
  const { notifyError } = useNotifications();

  const [showSlideout, setShowSlideout] = useState(false);
  const [loadingId, setLoadingId] = useState<string>();

  const [tableData, setTableData] = useState<TdsGeneratedAssetSchema[]>([]);

  const [generatedAsset, setGeneratedAsset] =
    useState<TdsGeneratedAssetSchema>();

  const [assetCategories, setAssetCategories] = useState<
    OptionType<SupportedAssetCategoryType>[]
  >([]);

  const { data: template, error: templateFetchError } = useSWR<
    AttributeTemplateSchema,
    AxiosError
  >(`/v2/tenants/${tenant_id}/pim/templates/${template_id}`);

  const templateDisplayName = getTemplateDisplayName(
    template ?? {},
    t("Template")
  );

  const {
    data: tdsGeneratedAsset,
    error: tdsGeneratedAssetError,
    mutate,
  } = useSWR<TdsGeneratedAssetSchema[], AxiosError>(
    endpoints.v2_tenants_tenant_id_templates_template_id_generated_assets(
      tenant_id,
      template_id
    )
  );

  useSWR<AssetCategory>(
    `${endpoints.v2_tenants_id_pim_assets_categories(
      tenant_id
    )}?asset_type=document`,
    {
      onSuccess: ({ asset_categories }) =>
        setAssetCategories(
          asset_categories.map((category) => ({
            label: category.name,
            value: category.id,
          }))
        ),
    }
  );

  const filteredAssetCategories = useMemo(() => {
    if (assetCategories.length === 0 || !tdsGeneratedAsset) {
      return assetCategories;
    }
    const generatedAssetCategoriesSet = new Set(
      tdsGeneratedAsset.map((asset) => asset.asset_category)
    );
    return assetCategories.filter(
      (category) =>
        !generatedAssetCategoriesSet.has(
          category.label as SupportedAssetCategoryType
        )
    );
  }, [assetCategories, tdsGeneratedAsset]);

  const updateGenerateAsset = useCallback(
    (is_active: boolean, generatedAsset: TdsGeneratedAssetSchema) => {
      const updateAsset = async () => {
        const { language, footer_content, title } = generatedAsset;
        const requestBody: TDSGeneratedAssetRequestSchema = {
          language,
          footer_content: footer_content ?? "",
          title,
          is_active,
        };
        try {
          setLoadingId(generatedAsset.id);
          await axios.patch<TdsGeneratedAssetSchema>(
            endpoints.v2_tenants_tenant_id_templates_template_id_generated_assets_id(
              tenant_id,
              template_id,
              generatedAsset.id
            ),
            requestBody
          );
          await mutate();
        } catch (error) {
          const errorMessage = (error as AxiosError)?.response?.data?.message;
          notifyError(
            errorMessage
              ? errorMessage
              : t("There was an error configuring the generated assets"),
            {
              error,
            }
          );
        } finally {
          setLoadingId(undefined);
        }
      };
      updateAsset();
    },
    [mutate, notifyError, t, template_id, tenant_id]
  );

  const tableColumns = useMemo(
    () => [
      {
        header: t("Asset Type"),
        accessorKey: "asset_type",
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <div>{toTitleCase(getValue())}</div>
        ),
      },
      {
        header: t("Category"),
        accessorKey: "asset_category",
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <div>{getValue() ?? "tds"}</div>
        ),
      },
      {
        header: t("No. of assets"),
        accessorKey: "number_of_assets",
      },
      {
        header: `${t("Enable")}/${t("Disable")}`,
        accessorKey: "is_active",
        cell: ({
          getValue,
          row: { original },
        }: {
          getValue: Getter<boolean>;
          row: { original: TdsGeneratedAssetSchema };
        }) =>
          loadingId === original.id ? (
            <LoadingIcon width={16} height={18} />
          ) : (
            <ToggleSwitch
              isChecked={getValue()}
              onClick={() => {
                updateGenerateAsset(!getValue(), original);
              }}
            />
          ),
      },
      {
        header: "",
        accessorKey: " ",
        cell: ({
          row: { original },
        }: {
          row: { original: TdsGeneratedAssetSchema };
        }) => (
          <EditButton
            onClick={() => {
              setGeneratedAsset(original);
              setShowSlideout(true);
            }}
          />
        ),
      },
    ],
    [loadingId, t, updateGenerateAsset]
  );

  useEffect(() => {
    if (tdsGeneratedAsset && tdsGeneratedAsset.length) {
      setTableData(
        tdsGeneratedAsset.map((generatedAsset) => ({
          ...generatedAsset,
          number_of_assets: generatedAsset?.number_of_assets ?? 0,
        }))
      );
    }
  }, [tdsGeneratedAsset]);

  const checkReverseMatch = (
    dynamicData: OptionType<SupportedAssetCategoryType>[],
    assets: TdsGeneratedAssetSchema[]
  ) => {
    const assetCategoryIds = new Set(assets.map((asset) => asset.category_id));

    const dynamicValues = dynamicData.map((item) => item.value);

    const missingIds = dynamicValues.filter(
      (value) => !assetCategoryIds.has(value)
    );

    if (missingIds.length === 0) return true;
    else return false;
  };

  const allDynamicCategoriesExistInAssets = checkReverseMatch(
    assetCategories,
    tdsGeneratedAsset || []
  );

  const isLoading =
    (!template && !templateFetchError) ||
    (!tdsGeneratedAsset && !tdsGeneratedAssetError);

  if (templateFetchError || tdsGeneratedAssetError) {
    return (
      <ErrorPlaceholder
        message={t(
          "There was an error fetching generated assets. Please try again later."
        )}
      />
    );
  }
  return isLoading ? (
    <DelayedSpinner />
  ) : (
    <PageWrapper>
      <Breadcrumb
        style={{ padding: `${spacingTokens.spacing_xl}`, paddingBottom: 0 }}
      >
        <LinkUnstyled
          to={{
            pathname: `${adminPath}/pim/templates`,
            state: {
              ...(location.state as TemplateLocationState),
              templateType:
                template?.template_type ??
                (location.state as TemplateLocationState)?.templateType,
            },
          }}
        >
          {t("Templates")}
        </LinkUnstyled>
        <div>/</div>
        <div>{templateDisplayName}</div>
      </Breadcrumb>
      <TemplatesNavWithPadding
        style={{ marginBottom: spacingTokens.spacing_xl }}
      >
        <TemplatesNav
          pageTitle={templateDisplayName}
          tabIndex={2}
          templateID={template_id}
          enableCustomForms={template?.enable_custom_forms}
        />
      </TemplatesNavWithPadding>
      <SlideOut
        title={t("Configure Generated Asset")}
        closeFlyout={() => setShowSlideout(false)}
        show={showSlideout}
      >
        <ConfigureGeneratedAssets
          template={template!}
          generatedAsset={generatedAsset}
          onSuccess={async () => {
            await mutate();
            setShowSlideout(false);
          }}
          assetCategories={filteredAssetCategories}
          onCancel={() => setShowSlideout(false)}
        />
      </SlideOut>
      <ContentWrapper style={{ padding: `0 ${spacingTokens.spacing_xl}` }}>
        <Table
          columns={tableColumns}
          isLoading={false}
          error={templateFetchError}
          Placeholder={
            <TablePlaceholder
              message={t(
                "No items to show. Please click the button below to add items."
              )}
            />
          }
          data={tableData}
          lastChildleftAlign
        />
        <PrimaryButtonWithPlusIcon
          style={{ marginTop: spacingTokens.spacing_3xl }}
          onClick={() => {
            setGeneratedAsset(undefined);
            setShowSlideout(true);
          }}
          disabled={allDynamicCategoriesExistInAssets}
        >
          {t("Document Configuration")}
        </PrimaryButtonWithPlusIcon>
      </ContentWrapper>
    </PageWrapper>
  );
};
